import { useState } from "react";
import { PageCollectionLayout } from "../../../../../components/pageCollection/PageCollectionLayout";
import { useMediaQuery } from "usehooks-ts";
import { useDebounce } from "use-debounce";
import { useShallow } from "zustand/shallow";
import { formatCurrency } from "@sellar-io/common";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import { Settings } from "../../../constants/tabs";
import { tabsLeft } from "../../../constants/tabs.shared";
import {
  SellarCreditStatementSortType,
  SellarCreditStatementSortValues,
} from "../../../stores/SellarCreditStatementTypes";
import { useSellarCreditStatementStore } from "../../../stores/SellarCreditStatementStore";
import { SellarCreditStatementTable } from "./components/SellarCreditStatementTable";
import { SellarCreditStatementCardList } from "./components/SellarCreditStatementCardList";
import { useStatementList } from "../../../../../hooks/api/v2/sellarCreditManagement/useStatementList";
import { SellarCreditStatementFilter } from "./components/SellarCreditStatementFilter";
import { capitalize } from "@sellar-io/common";
import { SellarCreditStatementStats } from "./components/SellarCreditStatementStats";
import { useHokodoCompanyStatement } from "../../../../../hooks/api/v2/sellarCreditManagement/useHokodoCompanyStatement";
import { TableHeader } from "../../../../../components/table/TableHeader";
import { SELLAR_CREDIT_STATEMENT } from "../../../../../constants/routes";
import { withSearchParams } from "../../../../../utils/withSearchParams";
import { SwitchCreditProviderButton } from "../../../components/SwitchCreditProviderButton";
import { useNavigate } from "react-router";
import { Tag } from "../../../../../components/tag/Tag";
import { DataButton, Icon, InputText } from "@sellar-io/components";
import { useFeatureFlagStore } from "~/hooks/useFeatureFlags";

export const HokodoSellarCreditStatement = () => {
  const [desktopFilter, setDesktopFilter] = useState(false);
  const [mobileFilter, setMobileFilter] = useState(false);
  const [clickOutside, setClickOutside] = useState(true);

  const isDesktop = useMediaQuery("(min-width: 1200px)");

  const state = useSellarCreditStatementStore(useShallow((state) => state));
  const scPaymentAllocationHistoryEnabled = useFeatureFlagStore().isActive(
    "SELLAR_CREDIT_PAYMENT_ALLOCATION_HISTORY",
  );

  const storeActions = state;

  const handleTableSort = (
    columnSort: SellarCreditStatementSortType | null,
  ) => {
    storeActions.setColumnSort(columnSort ?? "createdAt_desc");
  };

  const [debouncedSearchValue] = useDebounce(state.search, 500);

  const documentTotalMinNum = parseInt(state.documentTotalMin);
  const documentTotalMaxNum = parseInt(state.documentTotalMax);

  const queryParams = {
    search: debouncedSearchValue,
    sort: SellarCreditStatementSortValues[state.columnSort]
      ? [SellarCreditStatementSortValues[state.columnSort].mutation]
      : [],
    query: [
      ...(state.business
        ? [
            {
              type: "businessId",
              modifier: "EQUALS",
              value: state.business.id,
            } as const,
          ]
        : []),
      ...(state.user
        ? [
            {
              type: "userId",
              modifier: "EQUALS",
              value: state.user.id,
            } as const,
          ]
        : []),
      ...(state.paymentStatuses.length > 0
        ? [
            {
              type: "paymentStatus",
              modifier: "IN",
              value: state.paymentStatuses,
            } as const,
          ]
        : []),
      ...(!!state.documentTotalMin || !!state.documentTotalMax
        ? [
            {
              type: "documentTotalValue",
              modifier: "BETWEEN",
              value: {
                low: isNaN(documentTotalMinNum) ? 0 : documentTotalMinNum * 100,
                high: isNaN(documentTotalMaxNum)
                  ? 999999999
                  : documentTotalMaxNum * 100,
              },
            } as const,
          ]
        : []),
      ...(!!state.documentMonth
        ? [
            {
              type: "documentDate",
              modifier: "BETWEEN",
              value: {
                low: dayjs(state.documentMonth).startOf("month").toDate(),
                high: dayjs(state.documentMonth).endOf("month").toDate(),
              },
            } as const,
          ]
        : []),
      ...(!!state.excludeCreditNotes
        ? [
            {
              type: "excludeCreditNotes",
              modifier: "EQUALS",
              value: state.excludeCreditNotes,
            } as const,
          ]
        : []),
      {
        type: "creditProvider",
        modifier: "EQUALS",
        value: "hokodo",
      } as const,
    ],
  };

  const invoiceQuery = useStatementList(queryParams, {
    refetchOnWindowFocus: false,
  });

  const flatData =
    invoiceQuery.data?.pages.flatMap((page) => page.map((row) => row)) ?? [];

  const filteredTotalOutstanding = flatData.reduce((prev, invoice) => {
    if (invoice.type === "INV" && invoice.status !== "void") {
      return prev + invoice.total;
    }
    return prev;
  }, 0);

  const filtersApplied =
    state.business !== null ||
    state.user !== null ||
    state.documentTotalMin !== "" ||
    state.documentTotalMax !== "" ||
    state.excludeCreditNotes ||
    state.paymentStatuses.length > 0;

  const { data: companyStatements } = useHokodoCompanyStatement();

  console.log("SD", companyStatements);

  const monthsStatement = companyStatements?.find(
    (statement) =>
      dayjs(statement.endDate).format("YYYY-MM") ===
      dayjs(state.documentMonth).format("YYYY-MM"),
  );

  const monthTotal =
    (monthsStatement?.outstandingAmount ?? 0) +
    (monthsStatement?.settlementsTotal ?? 0);

  const total = (() => {
    if (monthsStatement?.outstandingAmount === monthTotal) {
      return formatCurrency(monthTotal);
    }

    return (
      <>
        {formatCurrency(monthsStatement?.outstandingAmount ?? 0)}
        <span className="text-sm font-normal">
          / {formatCurrency(monthTotal)}
        </span>
      </>
    );
  })();

  const changeMonth = (forward: boolean) => {
    storeActions.setDocumentMonth(
      dayjs(state.documentMonth)
        .add(forward ? 1 : -1, "months")
        .format("YYYY-MM"),
    );
  };

  const statement = (() => {
    const statementMonth = dayjs(state.documentMonth);
    const lastDay = statementMonth.daysInMonth();
    const month = statementMonth.format("MMMM");
    const year = statementMonth.format("YYYY");

    return `${month} 1-${lastDay}, ${year}`;
  })();

  const status = (() => {
    const outstandingAmount = monthsStatement?.outstandingAmount ?? 0;
    const settlementsTotal = monthsStatement?.settlementsTotal ?? 0;
    const today = dayjs();
    const afterDueDate = today.isAfter(dayjs(monthsStatement?.dueDate));
    const dueDatePlusFour = today.isAfter(
      dayjs(monthsStatement?.dueDate).add(4, "days"),
    );
    const afterEndDate = today.isAfter(dayjs(monthsStatement?.endDate));
    if (dueDatePlusFour && outstandingAmount > 0) return "Overdue";
    if (afterDueDate && outstandingAmount > 0) return "Processing";
    if (settlementsTotal > 0 && outstandingAmount > 0) return "Partially paid";
    if (settlementsTotal > 0 && outstandingAmount === 0) return "Paid";
    if (settlementsTotal === 0 && outstandingAmount > 0 && afterEndDate)
      return "Issued";
    return "Not yet issued";
  })();

  const navigate = useNavigate();

  return (
    <PageCollectionLayout
      title="Sellar Credit"
      topComponent={
        <SwitchCreditProviderButton
          onSwitchClick={() =>
            navigate(
              withSearchParams(SELLAR_CREDIT_STATEMENT, { provider: "two" }),
            )
          }
          currentProvider="hokodo"
        />
      }
      tabsLeft={tabsLeft({
        provider: "hokodo",
        scPaymentAllocationHistoryEnabled,
      })}
      rightComponent={<Settings provider="hokodo" />}
    >
      <div
        className={twMerge(
          "container mx-auto flex max-w-[1300px] flex-col bg-zinc-100",
          isDesktop ? "mt-6" : "mt-0",
        )}
      >
        <SellarCreditStatementStats
          statement={statement}
          dueDate={monthsStatement?.dueDate ?? null}
          total={total}
          status={status}
        />

        <TableHeader
          bottomRightComponent={
            filtersApplied && (
              <>
                <span className="font-semibold">
                  Total with filter(s) applied:
                </span>{" "}
                {formatCurrency(filteredTotalOutstanding)}
              </>
            )
          }
          className="mt-4"
          clickOutside={clickOutside}
          desktopFilterActive={desktopFilter}
          setDesktopFilterActive={setDesktopFilter}
          filter={
            <SellarCreditStatementFilter
              onDismiss={() => {
                setDesktopFilter(false);
                setMobileFilter(false);
              }}
              setClickOutside={(value?: boolean) =>
                setClickOutside(value ?? !clickOutside)
              }
            />
          }
          mobileFilterActive={mobileFilter}
          setMobileFilterActive={setMobileFilter}
          rightComponent={
            <>
              <DataButton
                onClick={() => changeMonth(false)}
                border={true}
                className="px-1"
                disabled={state.documentMonth === "2025-02"}
              >
                <Icon icon="CHEVRON_LEFT" />
              </DataButton>

              <InputText
                value={state.documentMonth}
                onChange={(e) => storeActions.setDocumentMonth(e.target.value)}
                min="2025-02"
                type="month"
                className="relative flex w-fit cursor-pointer items-center justify-center gap-2 rounded-lg border border-solid border-zinc-300 bg-white px-3 py-2 text-center text-sm leading-5 font-semibold tracking-wide whitespace-nowrap not-italic shadow-[0_2px_2px_0_rgba(0,0,0,0.15)] transition-colors first-of-type:ml-0 last-of-type:mr-0 hover:bg-zinc-100 motion-reduce:transition-none"
              />

              <DataButton
                onClick={() => changeMonth(true)}
                border={true}
                className="px-1"
                disabled={state.documentMonth === dayjs().format("YYYY-MM")}
              >
                <Icon icon="CHEVRON_RIGHT" />
              </DataButton>
            </>
          }
          search={state.search}
          searchPlaceholder="Search #, supplier, business, user..."
          setSearch={storeActions.setSearch}
          tags={
            <>
              {!!state.business && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => storeActions.setBusiness(null)}
                  icon="FACTORY"
                >
                  {state.business.name}
                </Tag>
              )}
              {!!state.user && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => storeActions.setUser(null)}
                  icon="PROFILE"
                >
                  {state.user.name}
                </Tag>
              )}
              {state.paymentStatuses.length > 0 && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => storeActions.setPaymentStatuses([])}
                  icon="FILE_DOCUMENT_CHECK_OUTLINE"
                >
                  {state.paymentStatuses
                    .map((status) => capitalize(status))
                    .join(", ")}
                </Tag>
              )}
              {(!!state.documentTotalMin || !!state.documentTotalMax) && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => {
                    storeActions.setDocumentTotalMin("");
                    storeActions.setDocumentTotalMax("");
                  }}
                  icon="CART"
                >
                  {`Document total ${state.documentTotalMin ? formatCurrency(parseInt(state.documentTotalMin) * 100) : "up to"} ${!!state.documentTotalMax && !!state.documentTotalMin && state.documentTotalMin !== "" ? "-" : !!state.documentTotalMin && !state.documentTotalMax ? "+" : ""} ${!!state.documentTotalMax ? formatCurrency(parseInt(state.documentTotalMax) * 100) : ""}`}
                </Tag>
              )}
              {!!state.excludeCreditNotes && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => storeActions.setExcludeCreditNotes(false)}
                  icon="NOTE_OFF_OUTLINE"
                >
                  Exclude credit notes
                </Tag>
              )}
            </>
          }
        />
        {isDesktop ? (
          <SellarCreditStatementTable
            handleTableSort={handleTableSort}
            query={invoiceQuery}
          />
        ) : (
          <SellarCreditStatementCardList query={invoiceQuery} />
        )}
      </div>
    </PageCollectionLayout>
  );
};
